import type { BlogPost, SitePageSummary, VisitorConfig } from '../../types';
import { SiteHeader } from './PageRenderer';
import { SiteFooter } from './SiteFooter';
import { SectionRenderer } from './SectionRenderer';
import { SiteWidgets } from './SiteWidgets';
import { SocialShareButtons } from './SocialShareButtons';
import { useDocumentMeta } from '../../utils/useDocumentMeta';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

interface BlogArticleProps {
  config: VisitorConfig;
  post: BlogPost;
  menuPages: SitePageSummary[];
  /** Domaine principal de l'agence, pour les liens de partage — voir SocialShareButtons. */
  siteUrl: string;
}

/** Un article de blog publié — voir /blog/:slug. */
export function BlogArticle({ config, post, menuPages, siteUrl }: BlogArticleProps) {
  useDocumentMeta(
    `${post.title} — ${config.brandLogoText}`,
    post.metaDescription || post.excerpt || undefined
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SiteHeader config={config} menuPages={menuPages} />

      <main className="max-w-3xl mx-auto px-4 py-12 flex-1 w-full">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <a href="/blog" className="text-xs font-bold text-gray-400 hover:opacity-70">
            ← Retour au blog
          </a>
          <SocialShareButtons url={`${siteUrl}/blog/${post.slug}`} title={post.title} />
        </div>

        <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide mt-6 mb-1">
          {formatDate(post.publishedAt)}
          {post.authorName && ` — ${post.authorName}`}
        </p>
        <h1 className="text-3xl font-black leading-tight" style={{ color: config.brandColor }}>
          {post.title}
        </h1>
        {post.excerpt && <p className="text-base text-gray-500 mt-3">{post.excerpt}</p>}

        {post.coverImageUrl && (
          <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden mt-6">
            <img src={post.coverImageUrl} alt="" className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex flex-wrap mt-8">
          {post.sections.map((section) => (
            <SectionRenderer key={section.id} section={section} />
          ))}
        </div>
      </main>

      <SiteFooter config={config} menuPages={menuPages} />
      <SiteWidgets config={config} />
    </div>
  );
}
